// Hidden animation playground (not in the nav). Open /lab to eyeball the boar
// and loader animations without triggering a real sync. Purely decorative -
// no data, no auth needed.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { RotateCw, PlayCircle } from 'lucide-react';
import BoarMark from '../components/BoarMark';
import RunningBoar from '../components/RunningBoar';
import CelebratingBoar from '../components/CelebratingBoar';
import PetanqueLoader from '../components/PetanqueLoader';
import { cn } from '../lib/utils';

const SIZES = [48, 72, 120];

function Card({ title, hint, children }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-card">
      <div className="mb-1 text-[13px] font-semibold">{title}</div>
      {hint && <div className="mb-3 text-[12px] text-muted-foreground">{hint}</div>}
      <div className="grid min-h-[140px] place-items-center rounded-lg bg-muted/40 p-4">
        {children}
      </div>
    </div>
  );
}

export default function BoarLab() {
  const [replay, setReplay] = useState(0);   // bump to remount → restart CSS animations
  const [size, setSize] = useState(120);
  const [gallop, setGallop] = useState(false);

  return (
    <div className="min-h-screen bg-background p-6 text-foreground sm:p-10">
      {/* the across-the-top gallop (fires after a real sync) */}
      {gallop && (
        <div className="animate-boar-run pointer-events-none fixed left-0 top-2 z-[200]"
             onAnimationEnd={() => setGallop(false)}>
          <RunningBoar className="drop-shadow-md" />
        </div>
      )}

      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <BoarMark size={28} rounded={8} /> Animation lab
          </h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Hidden page for eyeballing the boar &amp; loaders — no sync required.
          </p>
        </div>
        <Link to="/suppliers" className="text-[13px] text-primary hover:underline">← Back to Sangly</Link>
      </header>

      {/* controls */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <button onClick={() => setReplay((v) => v + 1)}
                className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-[13px] font-medium hover:bg-muted">
          <RotateCw className="h-4 w-4" /> Replay animations
        </button>
        <button onClick={() => { setGallop(false); requestAnimationFrame(() => setGallop(true)); }}
                className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-[13px] font-medium hover:bg-muted">
          <PlayCircle className="h-4 w-4" /> Gallop across the top
        </button>
        <span className="ml-2 text-[12px] text-muted-foreground">Size</span>
        {SIZES.map((s) => (
          <button key={s} onClick={() => setSize(s)}
                  className={cn('h-9 rounded-md border px-3 text-[13px] font-medium transition-colors',
                    size === s ? 'border-primary bg-primary/12 text-foreground' : 'border-border bg-card text-muted-foreground hover:bg-muted')}>
            {s}px
          </button>
        ))}
      </div>

      <div key={replay} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card title="Sync done · beer 🍺" hint="Random celebration after a successful sync.">
          <CelebratingBoar mood="cheers" size={size} />
        </Card>
        <Card title="Sync done · pétanque 😎" hint="…or throws a boule like a boss.">
          <CelebratingBoar mood="petanque" size={size} />
        </Card>
        <Card title="Refreshing · running boar" hint="Gallops in place while data is pulled.">
          <RunningBoar className="drop-shadow-sm" />
        </Card>
        <Card title="Pétanque loader (boule)" hint="Spinner used across the app.">
          <div className="flex items-center gap-6">
            <PetanqueLoader size={Math.min(size, 72)} />
            <div className="text-center">
              <PetanqueLoader size={Math.min(size, 72)} roll />
              <div className="mt-2 text-[11px] text-muted-foreground">roll</div>
            </div>
          </div>
        </Card>
        <Card title="Logo (BoarMark)" hint="The app mark / favicon.">
          <BoarMark size={Math.min(size, 96)} rounded={Math.min(size, 96) * 0.28} />
        </Card>
        <Card title="Reduced motion" hint="All of the above freeze under prefers-reduced-motion.">
          <div className="text-center text-[12px] text-muted-foreground">
            Toggle your OS “Reduce motion” setting,<br />then hit Replay to confirm everything stills.
          </div>
        </Card>
      </div>
    </div>
  );
}
